// Author: @venkatesh1146 😉
// Github : https://github.com/venkatesh1146

import {useCallback, useState} from 'react';
import PropTypes from 'prop-types';

function useRestApi({apiFunction, options = {}}) {
  const [state, setState] = useState({
    status: 'idle',
    data: undefined,
    error: null,
    isIdle: true,
    isLoading: false,
    isSuccess: false,
    isError: false,
  });

  const reset = useCallback(() => {
    setState({
      status: 'idle',
      data: undefined,
      error: null,
      isIdle: true,
      isLoading: false,
      isSuccess: false,
      isError: false,
    });
  }, []);

  const doApiCall = useCallback(
    async (variables) => {
      let context;

      try {
        // Set loading state
        setState((prev) => {
          return {
            ...prev,
            status: 'loading',
            isLoading: true,
            isIdle: false,
            isSuccess: false,
            isError: false,
            error: null,
          };
        });

        // Call onMutate if provided
        if (options.onMutate) {
          context = await options.onMutate(variables);
        }

        // Execute the API function
        const data = await apiFunction(variables);

        // Set success state
        setState((prev) => {
          return {
            ...prev,
            status: 'success',
            data,
            isLoading: false,
            isSuccess: true,
            isError: false,
            error: null,
          };
        });

        // Call onSuccess if provided
        if (options.onSuccess) {
          options.onSuccess(data, variables, context);
        }

        // Call onSettled if provided
        if (options.onSettled) {
          options.onSettled(data, null, variables, context);
        }

        return data;
      } catch (error) {
        // Set error state
        setState((prev) => {
          return {
            ...prev,
            status: 'error',
            error,
            isLoading: false,
            isSuccess: false,
            isError: true,
          };
        });

        // Call onError if provided
        if (options.onError) {
          options.onError(error, variables, context);
        }

        // Call onSettled if provided
        if (options.onSettled) {
          options.onSettled(undefined, error, variables, context);
        }

        throw error;
      }
    },
    [apiFunction, options]
  );

  const doAPiCallAsync = useCallback(async (variables) => doApiCall(variables), [doApiCall]);

  return {
    ...state,
    doApiCall,
    doAPiCallAsync,
    isLoading: state.isLoading,
    reset,
  };
}

// PropTypes for type checking
useRestApi.propTypes = {
  apiFunction: PropTypes.func.isRequired,
  options: PropTypes.shape({
    onMutate: PropTypes.func,
    onSuccess: PropTypes.func,
    onError: PropTypes.func,
    onSettled: PropTypes.func,
  }),
};

useRestApi.defaultProps = {
  options: {},
};

export default useRestApi;
